import type { CSSProperties, FC, PropsWithChildren } from 'react';
import { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import ConfigContext from 'app/common/ConfigContext';
import DropdownPeriod from 'app/components/DropdownPeriod';
import Error from 'app/components/Error';
import TeamAvatar from 'app/components/TeamAvatar';
import ArrowIcon from 'app/icons/arrow-long.svg';
import DisplayContext from 'app/common/DisplayContext';

import style from './style.module.scss';

interface Props extends ClassNameProp, PropsWithChildren {
  title?: string;
  subtitle?: string;
  withPeriodSelector?: boolean;
  backButtonCallback?: () => void;
  opponentsLogo?: [OpponentBase | undefined, OpponentBase | undefined];
  containerClassname?: string;
  hasData?: boolean;
  errorType?: BlockErrorType;
  subtitleClassName?: string;
  titleClassName?: string;
  contentStyle?: CSSProperties;
  testId?: string;
}

const BlockTemplate: FC<Props> = ({
  title,
  subtitle,
  children,
  withPeriodSelector = false,
  backButtonCallback,
  opponentsLogo,
  containerClassname,
  className,
  hasData = true,
  errorType = 'no-data',
  subtitleClassName,
  titleClassName,
  contentStyle,
  testId
}) => {
  const { period_edit, title: withTitle } = useContext(ConfigContext);
  const { singleBlockDisplay, displayedBlocks } = useContext(DisplayContext);

  const { t } = useTranslation();

  const shouldDisplayBlock = hasData || period_edit;

  if (!singleBlockDisplay && !shouldDisplayBlock && displayedBlocks.length > 1) {
    return null;
  }

  const displayHeader =
    title || subtitle || backButtonCallback || (withPeriodSelector && period_edit);
  const displayTitle = title && (!singleBlockDisplay || withTitle);

  return (
    <section
      className={classNames(style.blockTemplate, className, 'override-block-template')}
      data-testid={testId}
    >
      {displayHeader && (
        <header className={style.blockHeader}>
          <div className={style.blockHeaderTop}>
            <div className={style.blockTitleContainer}>
              {backButtonCallback && (
                <button
                  type="button"
                  onClick={backButtonCallback}
                  className={style.backButton}
                  aria-label={t('all-back_button', { section: title ?? t('all-back_button-prev') })}
                >
                  <ArrowIcon />
                </button>
              )}
              {displayTitle && (
                <h2 className={classNames(style.blockTitle, titleClassName)}>{title}</h2>
              )}
            </div>
            {withPeriodSelector && period_edit && <DropdownPeriod />}
          </div>
          {subtitle && (
            <h3 className={classNames(style.blockSubtitle, subtitleClassName)}>{subtitle}</h3>
          )}
        </header>
      )}
      {opponentsLogo && hasData && (
        <div className={style.teamLogosContainer} aria-hidden>
          <div className={style.teamLogosWrapper}>
            {opponentsLogo[0] ? (
              <TeamAvatar
                team={opponentsLogo[0]}
                className={style.teamLogoBackgroundLeft}
                noPlaceholder
              />
            ) : (
              <div />
            )}
            {opponentsLogo[1] ? (
              <TeamAvatar
                team={opponentsLogo[1]}
                className={style.teamLogoBackgroundRight}
                noPlaceholder
              />
            ) : (
              <div />
            )}
          </div>
        </div>
      )}
      <div className={classNames(style.blockContent, containerClassname)} style={contentStyle}>
        {hasData ? children : <Error type={errorType} />}
      </div>
    </section>
  );
};

BlockTemplate.displayName = 'BlockTemplate';

export default BlockTemplate;
